import {
  Alert,
  AlertDescription,
  AlertTitle,
  Stack,
} from '@hurdl/design-system';
import {
  type DeclaredPageKey,
  type PageAvailability,
} from '@hurdl/admin-panel-backend/product';
import { type ReactElement } from 'react';

/** The human label and the one-line purpose of each Declared_Page. */
const PAGE_LABEL: Record<DeclaredPageKey, string> = {
  overview: 'Overview',
  financials: 'Financials',
  support: 'Support',
  users: 'Users',
  disputes: 'Disputes',
  'email-templates': 'Email templates',
};

/** The reason a page is unavailable, as a sentence an Operator reads. */
const REASON_SENTENCE: Record<string, string> = {
  'capability-absent':
    'The capability this page reads through has not been deployed yet.',
  'not-configured': 'This product declares no source for this page.',
  'source-unreachable': 'The source for this page did not respond.',
};

/**
 * A Declared_Page, rendered availability-aware (R14.2, R14.4).
 *
 * In a full build this renders the composed capability's components against the
 * Product's declaration. In this build the Product-facing capabilities are not
 * composed, so every Declared_Page is `capability-absent` and renders as **unavailable
 * with its reason** rather than failing to start or showing an empty page — the
 * unavailable-not-crash the availability model is for. A glance never reads an outage
 * as a quiet period.
 */
export function DeclaredPage({
  pageKey,
  availability,
}: {
  readonly pageKey: DeclaredPageKey;
  readonly availability: PageAvailability;
}): ReactElement {
  const label = PAGE_LABEL[pageKey];

  if (availability.state === 'available') {
    return (
      <Stack gap="medium">
        <h1 className="font-hurdl-display text-hurdl-heading font-hurdl-semibold">
          {label}
        </h1>
        <p className="text-hurdl-muted-foreground">
          This page composes its capability&rsquo;s components here.
        </p>
      </Stack>
    );
  }

  return (
    <Stack gap="medium">
      <h1 className="font-hurdl-display text-hurdl-heading font-hurdl-semibold">
        {label}
      </h1>
      <Alert tone="informational">
        <AlertTitle>Unavailable</AlertTitle>
        <AlertDescription>
          {REASON_SENTENCE[availability.reason] ?? 'This page is unavailable.'}
        </AlertDescription>
      </Alert>
    </Stack>
  );
}
